import { useNavigate, useRouterState } from '@tanstack/react-router';
import { Box } from 'components/Box';
import { Button } from 'components/Button';
import ExternalLink from 'components/ExternalLink/ExternalLink.component';
import Icon from 'components/Icon/Icon.component';
import { APP_COUNTRY } from 'config';
import ArrowDownWhite from 'images/icons/chevron-down-white.svg?react';
import ArrowUpWhite from 'images/icons/chevron-up-white.svg?react';
import emptyFavoriteSrc from 'images/icons/pink-heart-empty.svg';
import fullFavoriteSrc from 'images/icons/pink-heart-full.svg';
import UserIconComponent from 'images/icons/user.svg?react';
import React from 'react';
import { Translate } from 'react-localize-redux';
import { useSelector, useStore } from 'react-redux';
import { requestDataIfNecessary } from 'services/api/api.actions';
import { selectNavbarContent } from 'services/cmsContent/cmsContent.selectors';
import {
  makeSelectPostFavoritesApiConfig,
  selectFavorites,
} from 'services/favorites/favorites.selectors';
import { makeSelectPostGrantApiConfig } from 'services/grant/grant.selectors';
import {
  selectAuth,
  selectVerifySessionEndpointApiConfig,
} from 'services/session/session.selectors';
import { idTracingMap } from 'utils/config.config';
import useGetTranslate from 'utils/hooks/useGetTranslate';
import { VariantComponentProps } from '../SignedInUserToolbar.type';
import {
  BalanceDisplay,
  BonusLink,
  CurrencyLabel,
  DropdownChevron,
  NotificationLabel,
  ToolbarButton,
  UserWallet,
  VerticalSeparator,
} from '../style/SignedInUserToolbar.style';

const country = APP_COUNTRY;

const ExpandedVariant: React.FC<VariantComponentProps> =
  function ExpandedVariant({
    formattedBalance,
    formattedBonus,
    onDropdownToggle,
    onPanelToggle,
    isPanelExpanded,
  }) {
    const reduxStore = useStore();
    const navbarContent = useSelector(selectNavbarContent);
    const favorites = useSelector(selectFavorites);
    const navigate = useNavigate();
    const { location } = useRouterState();
    const t = useGetTranslate();

    const handleChangeLocationToDashboard = () => {
      if (location.pathname !== '/') {
        navigate({ to: '/' });
      }
    };

    const onRechargeClick = () => {
      navigate({ to: '/topup' });
    };

    const auth = useSelector(selectAuth);

    React.useEffect(() => {
      const verifySessionApiConfig = selectVerifySessionEndpointApiConfig(
        reduxStore.getState(),
      );

      reduxStore.dispatch(requestDataIfNecessary(verifySessionApiConfig));
    }, [reduxStore]);

    React.useEffect(() => {
      if (auth.requestId) {
        const postGrantApiConfig = makeSelectPostGrantApiConfig({
          requestId: auth.requestId,
        })(reduxStore.getState());
        reduxStore.dispatch(requestDataIfNecessary(postGrantApiConfig));
      }
    }, [auth.requestId, reduxStore]);

    React.useEffect(() => {
      const postFavoritesApiConfig = makeSelectPostFavoritesApiConfig()(
        reduxStore.getState(),
      );
      reduxStore.dispatch(requestDataIfNecessary(postFavoritesApiConfig));
    }, [reduxStore]);

    const handleDropdownClick = () => {
      const newValue = !isPanelExpanded;
      onDropdownToggle?.(newValue);
      onPanelToggle?.(newValue);
    };

    return (
      <Box
        display={'flex'}
        alignItems={'center'}
        height={'100%'}
        padding="0 20px 0 0"
        backgroundColor={'var(--site-header-toolbar)'}
        color={'white'}
        position={'relative'}
        zIndex={isPanelExpanded ? 1001 : 'auto'}
      >
        <UserWallet>
          <BalanceDisplay>
            <NotificationLabel textAlign="left">
              {navbarContent.labelMyBalance}
            </NotificationLabel>
            <CurrencyLabel
              data-qa="user-toolbar-bonus"
              id={idTracingMap.SignedInUserToolbar.EV_Balance}
            >
              {formattedBalance || ' '}
            </CurrencyLabel>
          </BalanceDisplay>
          <VerticalSeparator />
          <BonusLink
            noReferrer={false}
            target="_self"
            href={navbarContent.linkMyBonus}
          >
            <NotificationLabel textAlign="left">
              {navbarContent.labelMyBonus}
            </NotificationLabel>
            <CurrencyLabel
              data-qa="user-toolbar-bonus"
              id={idTracingMap.SignedInUserToolbar.EV_RealBonus}
            >
              {formattedBonus || ' '}
            </CurrencyLabel>
          </BonusLink>
          <DropdownChevron
            type="button"
            onClick={handleDropdownClick}
            title="Area Riservata"
          >
            <Icon
              svgIconComponent={isPanelExpanded ? ArrowUpWhite : ArrowDownWhite}
            />
          </DropdownChevron>
        </UserWallet>
        <Button
          data-qa="user-toolbar-recharge-button"
          id={idTracingMap.SignedInUserToolbar.EV_RechargeButton}
          height={'24px'}
          minWidth={'76px'}
          onClick={onRechargeClick}
        >
          <Translate id={'lbl.modalrecharge.recharge'} />
        </Button>
        <ExternalLink
          href={navbarContent?.linkFavorite || ''}
          noReferrer={false}
          target={'_self'}
          data-qa="user-toolbar-favorites-link"
          anchorElement={
            <ToolbarButton
              id={idTracingMap.SignedInUserToolbar.EV_HeartButton}
              padding="0 5px 0 5px"
              margin="0 3px 0 3px"
              as="a"
            />
          }
        >
          <Icon iconSrc={favorites ? fullFavoriteSrc : emptyFavoriteSrc} />
        </ExternalLink>
        <ToolbarButton
          id={idTracingMap.SignedInUserToolbar.EV_ProfileButton}
          type="button"
          padding="0 5px 0 5px"
          margin="0 3px 0 3px"
          onClick={handleChangeLocationToDashboard}
          title={t('reservedArea').toString()}
          data-qa="user-toolbar-profile-button"
        >
          <Icon svgIconComponent={UserIconComponent} />
        </ToolbarButton>
      </Box>
    );
  };

export default ExpandedVariant;
